import { useEffect, useState, type ChangeEvent } from "react";
import Modal from "@modalSrc/Modal";
import "@style/Admin.scss";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import { translateFromEnglishAsync } from "@/utils/translationService";
import { getFirstNonEnglishLanguageId } from "@/utils/languageUtils";

type Language = {
  id?: number | string;
  name?: string;
  abbrev?: string;
};

type Translation = {
  id?: number;
  key?: string;
  englishText?: string;
  languageId?: number | string;
  translation?: string;
  humanEdited: boolean;
};

type TranslationPayload = {
  id?: number;
  key?: string;
  englishText: string;
  languageId: number;
  translation: string;
};

type FieldTranslationModalProps = {
  isOpen: boolean;
  onClose: (savedId?: number) => void;
  translation?: Translation | null;
  languages: Language[];
  // onSave: (payload: TranslationPayload, isEdit: boolean) => Promise<void>;
};

export default function FieldTranslationModal({ isOpen, onClose, translation, languages }: FieldTranslationModalProps) {
  const [formData, setFormData] = useState({
    key: "",
    englishText: "",
    languageId: "",
    translation: ""
  });
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (translation) {
        // Edit mode
        setFormData({
          key: translation.key || "",
          englishText: translation.englishText || "",
          languageId: translation.languageId != null ? String(translation.languageId) : "",
          translation: translation.translation || ""
        });
      } else {
        const defaultLanguageId = getFirstNonEnglishLanguageId(languages);
        setFormData({
          key: "",
          englishText: "",
          languageId: defaultLanguageId > 0 ? String(defaultLanguageId) : "",
          translation: ""
        });
      }
    }
  }, [isOpen, translation, languages]);

  function handleInputChange(field: "key" | "englishText" | "languageId" | "translation", value: string) {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }

  async function handleSave() {
    if (!formData.englishText.trim() || !formData.languageId || !formData.translation.trim()) {
      toast.error("English Text, Language, and Translation are required");
      return;
    }

    const payload = {
      key: formData.key.trim() || undefined,
      englishText: formData.englishText.trim(),
      languageId: parseInt(formData.languageId),
      translation: formData.translation.trim(),
      id: translation?.id ?? 0
    } as TranslationPayload;

    try {
      let response = await RightTimeApi.sendAsync(`/FieldTranslation`, payload);
      if(response && response.ok) {
        const savedId = Number(response?.jsonData?.id || 0);
        toast.success(translation ? "Field translation updated successfully" : "Field translation added successfully");
        onClose(savedId > 0 ? savedId : undefined);
      } else {
        toast.error("Failed to save field translation - API is not responding");
      }
    } catch (error) {
      toast.error("Error saving field translation - API is not responding");
    }
  }

  async function handleTranslateAsync() {
    if (translation?.humanEdited) {
      toast.info("This translation was manually edited and cannot be auto-translated.");
      return;
    }
    if (!formData.englishText.trim()) {
      toast.error("English Text is required before translating.");
      return;
    }
    if (!formData.languageId) {
      toast.error("Please select a language before translating.");
      return;
    }

    const selectedLanguage = languages.find((lang) => String(lang.id) === String(formData.languageId));
    if (!selectedLanguage) {
      toast.error("Unable to resolve selected language.");
      return;
    }

    setIsTranslating(true);
    try {
      const translated = await translateFromEnglishAsync(formData.englishText, selectedLanguage);
      setFormData((prev) => ({
        ...prev,
        translation: translated,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to translate text.";
      toast.error(message);
    } finally {
      setIsTranslating(false);
    }
  }

  function handleClose() {
    setFormData({
      key: "",
      englishText: "",
      languageId: "",
      translation: ""
    });
    onClose();
  }

  if (!isOpen) {
    return null;
  }

  const footer = (
    <>
      <div className="footerLeft">
        <button
          className="btn btn-secondary"
          onClick={handleTranslateAsync}
          disabled={isTranslating || (translation?.humanEdited ?? false)}
          title={
            (translation?.humanEdited ?? false)
              ? "Manually edited translations cannot be auto-translated"
              : isTranslating
                ? "Translating..."
                : "Translate English into selected language"
          }
        >
          {isTranslating ? "Translating..." : "Translate"}
        </button>
      </div>
      <div className="footerRight">
        <button
          className="btn btn-secondary"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="fieldTranslationModal"
      title={translation ? "Edit Field Translation" : "Add New Field Translation"}
      footer={footer}
    >
      <div className="fieldTranslationForm">
        <div className="formGroup">
          <label>Key</label>
          <input
            type="text"
            value={formData.key}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("key", e.target.value)}
            placeholder="Optional stable key (e.g. request.submit)"
          />
        </div>

        <div className="formGroup">
          <label>English Text *</label>
          <input
            type="text"
            value={formData.englishText}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("englishText", e.target.value)}
            placeholder="English text to be translated"
          />
        </div>

        <div className="formGroup">
          <label>Language *</label>
          <select
            title="Language"
            value={formData.languageId}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => handleInputChange("languageId", e.target.value)}
          >
            <option value="">Select a language</option>
            {languages.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {lang.name} ({lang.abbrev})
              </option>
            ))}
          </select>
        </div>

        <div className="formGroup">
          <label>Translation *</label>
          <textarea
            value={formData.translation}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleInputChange("translation", e.target.value)}
            placeholder="Enter the translated text"
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
}


